import React, { useState, useEffect } from 'react';
import './ShipmentPage.css';
import Alert from './Alert';
import Button from './Button';
import CustomerSelect from './CustomerSelect';
import { DeviceIdPicker } from './DeviceIdPicker';
import LoadingSpinner from './LoadingSpinner';
import ReturnInventoryPicker, { ReturnLine } from './ReturnInventoryPicker';
import { API_BASE_URL } from '../config';
import { useCustomers } from '../context/CustomerContext';
import { displayName } from '../utils/customerDisplay';
import { clipUpsName, willUpsTruncate, UPS_NAME_MAX } from '../utils/upsName';
import { willUpsAddressTruncate, proposeUpsAddress, UPS_ADDRESS_LINE_MAX } from '../utils/upsAddress';

interface StripeInfo {
  name: string;
  email: string;
  subscription_status: string | null;
  contract_units: number | null;
  trial?: {
    end_date: string;
    days_remaining: number;
  } | null;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  address_country: string | null;
}

interface Customer {
  customer_tag: string;
  stripe: StripeInfo;
  stats: {};
}

interface Product {
  product_name: string;
  sku_code: string;
  available?: number;
}

interface PackageItem {
  sku_code: string;
  product_name: string | null;
  quantity: number;
}

interface ProductPackage {
  id: number;
  name: string;
  description: string | null;
  items: PackageItem[];
}

interface Shipment {
  tracking_number: string;
  cost: number;
  shipped_date: string;
  estimated_arrival: string;
  status: string;
  customer_tag: string;
  reference_number: string;
  sku_code: string;
  service_code: string;
  created_at: string;
  updated_at: string;
  is_return?: boolean;
  is_broken?: boolean;
  current_location?: string | null;
  carrier?: string;
  service_name?: string;
}

interface MultiCarrierRate {
  carrier: string;
  service_code: string;
  service_name: string;
  speed_tier: string;
  total_charges: number;
  delivery_date: string;
  estimated_days: number;
  is_cheapest_in_tier: boolean;
  is_recommended: boolean;
}

interface ProductSelection {
  product_name: string;
  quantity: number;
  device_ids?: string[];
  /** Stable across re-renders, so a row keeps its own React instance when an
   *  earlier row is removed. Never sent to the API. */
  lineId: string;
}

/** Row identity for React keys. Keying product rows by array index let a
 *  removal shift the next row's props into the removed row's component
 *  instance; DeviceIdPicker then saw a changed (customer, SKU) context and
 *  cleared a selection that was legitimately made on the surviving line. */
let lineIdCounter = 0;
const nextLineId = () => `line-${++lineIdCounter}`;

/**
 * Two controls can flip is_return — "Return Label" directly, and "Physical
 * Drop-off" indirectly (it forces is_return to false). Either flip swaps the
 * pool the device picker draws from (available <-> out), which makes any
 * already-selected device_ids meaningless — they were chosen under a pool
 * that no longer applies. Routing both handlers through this one helper is
 * what stops a third is_return-flipping control from reopening this gap.
 */
const clearDeviceIdsOnDirectionChange = (products: ProductSelection[]): ProductSelection[] =>
  products.map(p => ({ ...p, device_ids: [] }));

interface CustomAddress {
  name: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface ShippingRatesRequest {
  products: ProductSelection[];
  custom_address: CustomAddress | null;
}

interface CustomsItem {
  description: string;
  quantity: number;
  unit_value: number;
  hs_code: string;
}

interface CustomsDeclaration {
  products: CustomsItem[];
  reason_for_export: string;
}

interface ShipmentFormData {
  customer_tag: string;
  products: ProductSelection[];
  is_return: boolean;
  is_physical_dropoff: boolean;
  force_ground_shipping: boolean;
  shipping_speed: string;
  carrier: string;
  service_code: string;
  service_name: string;
  is_broken: boolean;
  create_inventory_order: boolean;
  use_custom_address: boolean;
  custom_address: CustomAddress;
  customs_declaration: CustomsDeclaration | null;
}

// Add US states array
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

const CA_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

const MX_STATES = [
  { code: 'AGU', name: 'Aguascalientes' },
  { code: 'BCN', name: 'Baja California' },
  { code: 'BCS', name: 'Baja California Sur' },
  { code: 'CAM', name: 'Campeche' },
  { code: 'CHP', name: 'Chiapas' },
  { code: 'CHH', name: 'Chihuahua' },
  { code: 'COA', name: 'Coahuila' },
  { code: 'COL', name: 'Colima' },
  { code: 'CDMX', name: 'Ciudad de México' },
  { code: 'DUR', name: 'Durango' },
  { code: 'GUA', name: 'Guanajuato' },
  { code: 'GRO', name: 'Guerrero' },
  { code: 'HID', name: 'Hidalgo' },
  { code: 'JAL', name: 'Jalisco' },
  { code: 'MEX', name: 'Estado de México' },
  { code: 'MIC', name: 'Michoacán' },
  { code: 'MOR', name: 'Morelos' },
  { code: 'NAY', name: 'Nayarit' },
  { code: 'NLE', name: 'Nuevo León' },
  { code: 'OAX', name: 'Oaxaca' },
  { code: 'PUE', name: 'Puebla' },
  { code: 'QUE', name: 'Querétaro' },
  { code: 'ROO', name: 'Quintana Roo' },
  { code: 'SLP', name: 'San Luis Potosí' },
  { code: 'SIN', name: 'Sinaloa' },
  { code: 'SON', name: 'Sonora' },
  { code: 'TAB', name: 'Tabasco' },
  { code: 'TAM', name: 'Tamaulipas' },
  { code: 'TLA', name: 'Tlaxcala' },
  { code: 'VER', name: 'Veracruz' },
  { code: 'YUC', name: 'Yucatán' },
  { code: 'ZAC', name: 'Zacatecas' },
];

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
];

const getRegionsForCountry = (country: string) => {
  switch (country) {
    case 'CA': return CA_PROVINCES;
    case 'MX': return MX_STATES;
    default: return US_STATES;
  }
};

const getRegionLabel = (country: string) => {
  switch (country) {
    case 'CA': return 'Province';
    case 'MX': return 'State';
    default: return 'State';
  }
};

const getPostalLabel = (country: string) => {
  return country === 'US' ? 'ZIP Code' : 'Postal Code';
};

const EXPORT_REASONS = ['SALE', 'GIFT', 'REPAIR', 'RETURN'];

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return 'N/A';
  try {
    // First try parsing as ISO string
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      // If invalid, try parsing MySQL datetime format
      const [datePart, timePart] = dateStr.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      
      // Month is 0-based in JavaScript Date
      const parsedDate = new Date(year, month - 1, day, hour, minute, second);
      if (isNaN(parsedDate.getTime())) {
        throw new Error('Invalid date format');
      }
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }).format(parsedDate);
    }
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', dateStr, error);
    return 'Invalid Date';
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatServiceCode = (code: string, serviceName?: string): string => {
  if (serviceName) return serviceName;
  const serviceNames: { [key: string]: string } = {
    '01': 'UPS Next Day Air',
    '02': 'UPS 2nd Day Air',
    '03': 'UPS Ground',
    '12': 'UPS 3 Day Select',
    '13': 'UPS Next Day Air Saver',
    '14': 'UPS Next Day Air Early',
    '59': 'UPS 2nd Day Air AM',
  };
  return serviceNames[code] || code;
};

const getTrackingUrl = (trackingNumber: string, carrier?: string): string => {
  switch (carrier) {
    case 'usps':
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    case 'fedex':
      return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    case 'ups':
    default:
      return `https://www.ups.com/track?tracknum=${trackingNumber}`;
  }
};

const getCarrierLabel = (carrier?: string): string => {
  switch (carrier) {
    case 'usps': return 'USPS';
    case 'fedex': return 'FedEx';
    case 'ups': return 'UPS';
    default: return 'UPS';
  }
};

const resizeImage = (base64: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // A4 dimensions in pixels at 96 DPI: 794 x 1123
      const A4_WIDTH = 794;
      const A4_HEIGHT = 1123;
      
      // Calculate 50% of A4 size
      const targetWidth = A4_WIDTH / 2;
      const targetHeight = A4_HEIGHT / 2;

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      // Use better quality scaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Draw image at 50% size
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      
      // Get resized base64 image
      resolve(canvas.toDataURL('image/gif'));
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = `data:image/gif;base64,${base64}`;
  });
};

const downloadLabel = async (gifBase64: string, fileName: string) => {
  try {
    // Add proper base64 prefix if not present
    const base64WithPrefix = gifBase64.startsWith('data:image/gif;base64,') 
      ? gifBase64 
      : `data:image/gif;base64,${gifBase64}`;
    
    // Create and trigger download
    const link = document.createElement('a');
    link.href = base64WithPrefix;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading label:', error);
    throw new Error('Failed to download shipping label');
  }
};

const getDayName = (date: Date): string => {
  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

const getMonthDay = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const ShipmentPage: React.FC = () => {
  const { customers, loading: customersLoading } = useCustomers();
  const [products, setProducts] = useState<Product[]>([]);
  const [packages, setPackages] = useState<ProductPackage[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showDelivered, setShowDelivered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallbackNotice, setFallbackNotice] = useState<{ message: string; reason: string } | null>(null);
  const [quantityErrors, setQuantityErrors] = useState<{[key: number]: string}>({});
  const [shippingRates, setShippingRates] = useState<MultiCarrierRate[]>([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [shipmentData, setShipmentData] = useState<ShipmentFormData>({
    customer_tag: '',
    products: [{ product_name: '', quantity: 0, lineId: nextLineId() }],
    is_return: false,
    is_physical_dropoff: false,
    force_ground_shipping: false,
    shipping_speed: '',
    carrier: '',
    service_code: '',
    service_name: '',
    is_broken: false,
    create_inventory_order: true,
    use_custom_address: false,
    custom_address: {
      name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    },
    customs_declaration: null
  });
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
  const [stateSuggestions, setStateSuggestions] = useState<Array<{code: string, name: string}>>([]);
  const [showStateSuggestions, setShowStateSuggestions] = useState(false);

  // Add shipping speed options
  const shippingSpeedOptions = [
    { code: '01', name: 'Next Day Air', description: 'Delivery by next business day' },
    { code: '02', name: '2nd Day Air', description: 'Delivery by second business day' },
    { code: '03', name: 'Ground', description: 'Most economical, 1-5 business days' },
    { code: '12', name: '3 Day Select', description: 'Delivery within 3 business days' },
    { code: '13', name: 'Next Day Air Saver', description: 'Next business day delivery by end of day' }
  ];

  // Modify the customer selection handler
  const handleCustomerSelect = (customerTag: string) => {
    setShipmentData(prev => ({
      ...prev,
      customer_tag: customerTag
    }));

    // Fetch shipping rates immediately when customer is selected
    if (customerTag) {
      // Use current products if any are selected, otherwise use a default product
      const productsToUse = shipmentData.products.some(p => p.product_name)
        ? shipmentData.products
        // Throwaway line used only to price a rate quote; it is never
        // rendered, but it still has to satisfy ProductSelection.
        : [{ product_name: products[0]?.product_name || '', quantity: 1,
             lineId: nextLineId() }];

      fetchShippingRates(customerTag, productsToUse);
    }
  };

  // Add validation for custom address
  const validateCustomAddress = () => {
    if (!shipmentData.use_custom_address) return true;
    
    const { name, street1, city, state, postal_code } = shipmentData.custom_address;
    // Block submission when street1 is over UPS's per-line limit: there is no
    // backend auto-clip for the street, so an over-length line is a hard UPS
    // rejection (error 120202). The rep is shown a proposed shorter address.
    return !!(name && street1 && city && state && postal_code) && !willUpsAddressTruncate(street1);
  };

  // Add validation for products
  // A product already chosen on another line is not offered again. Two lines
  // naming one product are a single order row -- every line of a shipment
  // shares its reference number, so `orders` cannot tell them apart -- and the
  // backend merges them on submit. Hiding the option says so up front instead
  // of silently rewriting what the rep entered.
  const productNamesInUse = shipmentData.products
    .map(p => p.product_name)
    .filter(Boolean);

  const validateProducts = () => {
    return shipmentData.products.some(p => p.product_name && p.quantity > 0);
  };

  // Add handler for custom address confirmation
  const handleConfirmCustomAddress = () => {
    if (!validateCustomAddress()) {
      setError('Please fill in all required address fields');
      return;
    }
    if (!validateProducts()) {
      setError('Please select a product and quantity');
      return;
    }
    
    if (shipmentData.customer_tag) {
      console.log('Fetching rates with custom address:', {
        use_custom_address: shipmentData.use_custom_address,
        custom_address: shipmentData.custom_address
      });
      fetchShippingRates(shipmentData.customer_tag, shipmentData.products);
    }
  };

  // Update fetchShippingRates to use custom address
  const fetchShippingRates = async (customerTag: string, productsToUse: ProductSelection[]) => {
    try {
      setRatesLoading(true);
      console.log('Fetching shipping rates for:', customerTag);
      console.log('Products data:', productsToUse);
      console.log('Current state:', {
        use_custom_address: shipmentData.use_custom_address,
        custom_address: shipmentData.custom_address
      });
      
      // Properly encode the customer tag for the URL
      const encodedCustomerTag = encodeURIComponent(customerTag);
      
      // Format the request data with proper typing
      const requestData = {
        products: productsToUse,
        is_return: shipmentData.is_return,
        custom_address: shipmentData.use_custom_address ? {
          name: shipmentData.custom_address.name,
          street1: shipmentData.custom_address.street1,
          street2: shipmentData.custom_address.street2 || '',
          city: shipmentData.custom_address.city,
          state: shipmentData.custom_address.state,
          postal_code: shipmentData.custom_address.postal_code,
          country: shipmentData.custom_address.country || 'US'
        } : null
      };

      const response = await fetch(`${API_BASE_URL}/api/shipping-rates/${encodedCustomerTag}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch shipping rates: ${errorData.error || 'Unknown error'}`);
      }

      const data = await response.json();
      setShippingRates(data.rates || []);

      // Auto-select the cheapest rate
      const allRates = data.rates || [];
      const cheapest = [...allRates].sort((a: MultiCarrierRate, b: MultiCarrierRate) => a.total_charges - b.total_charges)[0];
      const recommended = cheapest || allRates[0];
      if (recommended) {
        setShipmentData(prev => ({
          ...prev,
          carrier: recommended.carrier,
          service_code: recommended.service_code,
          service_name: recommended.service_name,
          shipping_speed: recommended.service_code,
        }));
      }
    } catch (err) {
      console.error('Error fetching shipping rates:', err);
      setShippingRates([]);  // Clear rates on error
    } finally {
      setRatesLoading(false);
    }
  };

  // Add refreshShipments function that combines refresh and fetch
  const refreshShipments = async () => {
    try {
      setRefreshing(true);
      
      // First refresh all shipment statuses
      const refreshResponse = await fetch(`${API_BASE_URL}/api/shipments/status/refresh-all`, {
        method: 'POST',
      });

      if (!refreshResponse.ok) {
        const data = await refreshResponse.json();
        throw new Error(data.error || 'Failed to refresh shipments');
      }

      // Then fetch updated shipments
      const fetchResponse = await fetch(`${API_BASE_URL}/api/shipments`);
      if (!fetchResponse.ok) throw new Error('Failed to fetch shipments');
      const data = await fetchResponse.json();
      console.log('Fetched shipments:', data.shipments);
      setShipments(data.shipments);
      
      // Update last refresh time
      setLastRefreshTime(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh shipments');
    } finally {
      setRefreshing(false);
    }
  };

  // Modify the fetchShipments function to check refresh time
  const fetchShipments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shipments`);
      if (!response.ok) throw new Error('Failed to fetch shipments');
      const data = await response.json();
      console.log('Fetched shipments:', data.shipments);
      setShipments(data.shipments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch shipments');
    }
  };

  const handleDeleteShipment = async (tracking_number: string) => {
    if (!window.confirm('Are you sure you want to void this shipment? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/shipments/${tracking_number}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to void shipment');
      }

      // Refresh shipments list
      await fetchShipments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void shipment');
    }
  };

  // Update handleRefreshAll to use the new refreshShipments function
  const handleRefreshAll = async () => {
    await refreshShipments();
  };

  const handleUpdateDropoffStatus = async (tracking_number: string) => {
    if (!window.confirm('Mark this shipment as delivered?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/shipments/${tracking_number}/dropoff-status`, {
        method: 'POST',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update dropoff status');
      }

      // Refresh shipments list
      await fetchShipments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update dropoff status');
    }
  };

  const handleRevertDropoff = async (tracking_number: string) => {
    if (!window.confirm('Revert this shipment back to pending status?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/shipments/${tracking_number}/revert-dropoff`, {
        method: 'POST',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to revert dropoff status');
      }

      // Refresh shipments list
      await fetchShipments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revert dropoff status');
    }
  };

  const handleDownloadLabel = async (tracking_number: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shipping-labels/${tracking_number}`);
      if (!response.ok) {
        throw new Error('Failed to download label');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipping-label-${tracking_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading label:', err);
      setError(err instanceof Error ? err.message : 'Failed to download label');
    }
  };

  // Modify the useEffect to include refresh logic
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch inventory data
        const inventoryResponse = await fetch(`${API_BASE_URL}/api/inventory`);
        if (!inventoryResponse.ok) throw new Error('Failed to fetch inventory');
        const inventoryData = await inventoryResponse.json();
        
        // Fetch products/SKUs and merge with inventory data
        const skusResponse = await fetch(`${API_BASE_URL}/api/skus`);
        if (!skusResponse.ok) throw new Error('Failed to fetch products');
        const skusData = await skusResponse.json();
        
        // Merge SKUs with inventory data to get available quantities
        const productsWithInventory = skusData.map((sku: Product) => {
          const inventoryItem = inventoryData.find((item: any) => item.sku_code === sku.sku_code);
          return {
            ...sku,
            available: inventoryItem ? inventoryItem.available : 0
          };
        });
        
        setProducts(productsWithInventory);

        try {
          const packagesResponse = await fetch(`${API_BASE_URL}/api/packages`);
          if (packagesResponse.ok) {
            setPackages(await packagesResponse.json());
          }
        } catch (pkgErr) {
          // Packages are optional UX; failing here shouldn't block the page.
          console.warn('Failed to load packages:', pkgErr);
        }

        // Just fetch shipments without refreshing status
        await fetchShipments();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Remove lastRefreshTime from dependencies

  const applyPackage = (packageId: string) => {
    setSelectedPackageId(packageId);
    if (!packageId) return;
    const pkg = packages.find(p => p.id === Number(packageId));
    if (!pkg || pkg.items.length === 0) return;

    const newProducts: ProductSelection[] = pkg.items
      .map(item => {
        const matched = products.find(prod => prod.sku_code === item.sku_code);
        const product_name = matched?.product_name || item.product_name || '';
        if (!product_name) return null;
        return { product_name, quantity: item.quantity, lineId: nextLineId() };
      })
      .filter((p): p is ProductSelection => p !== null);

    if (newProducts.length === 0) return;

    setShipmentData(prev => ({ ...prev, products: newProducts }));
    setQuantityErrors({});

    // Reset the picker so the same package can be re-applied later if needed.
    setTimeout(() => setSelectedPackageId(''), 0);
  };

  /** Lines chosen in the return picker. Existing lines keep their lineId and
   *  device selection (only quantity can have changed); new ones get fresh ids. */
  const applyReturnLines = (lines: ReturnLine[]) => {
    setShipmentData(prev => {
      const next: ProductSelection[] = lines.map(line => {
        const existing = prev.products.find(p => p.product_name === line.product_name);
        return existing
          ? { ...existing, quantity: line.quantity }
          : { product_name: line.product_name, quantity: line.quantity, lineId: nextLineId() };
      });
      const products = next.length > 0
        ? next
        : [{ product_name: '', quantity: 0, lineId: nextLineId() }];

      if (prev.customer_tag && next.length > 0) {
        if ((window as any)._rateDebounce) clearTimeout((window as any)._rateDebounce);
        const customerTag = prev.customer_tag;
        (window as any)._rateDebounce = setTimeout(() => {
          fetchShippingRates(customerTag, products);
        }, 500);
      }
      return { ...prev, products };
    });
    setQuantityErrors({});
  };

  const addProduct = () => {
    setShipmentData(prev => ({
      ...prev,
      products: [...prev.products, { product_name: '', quantity: 0, lineId: nextLineId() }]
    }));
    setQuantityErrors(prev => ({
      ...prev,
      [shipmentData.products.length]: ''
    }));
  };

  const removeProduct = (index: number) => {
    setShipmentData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
    setQuantityErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  // Also update product selection to refresh rates
  const updateProduct = (index: number, field: keyof ProductSelection, value: string | number) => {
    setShipmentData(prev => {
        const newProducts = prev.products.map((product, i) =>
            i === index
                // A device selection belongs to the SKU it was picked for —
                // changing the product on this line invalidates it, the same
                // way a stale reason/device selection was found to ride
                // along in InventoryPage's new-order form.
                ? { ...product, [field]: value, ...(field === 'product_name' ? { device_ids: [] } : {}) }
                : product
        );
        
        // Clear any existing error for this index
        setQuantityErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
        });

        // If updating quantity, validate against available inventory
        if (field === 'quantity' && typeof value === 'number') {
            const product = products.find(p => p.product_name === newProducts[index].product_name);
            if (product && !prev.is_return) {  // Only validate for outgoing shipments
                const available = product.available || 0;
                if (value > available) {
                    setQuantityErrors(prev => ({
                        ...prev,
                        [index]: `Exceeds available inventory (${available} available)`
                    }));
                }
            }
        }

        // Only fetch rates if we have a customer selected AND both product and quantity are set
        // Debounce to avoid firing on every keystroke while typing multi-digit numbers
        if (prev.customer_tag &&
            newProducts[index].product_name &&
            newProducts[index].quantity > 0) {
            if ((window as any)._rateDebounce) clearTimeout((window as any)._rateDebounce);
            const customerTag = prev.customer_tag;
            (window as any)._rateDebounce = setTimeout(() => {
                fetchShippingRates(customerTag, newProducts);
            }, 500);
        }

        return { ...prev, products: newProducts };
    });
  };

  // Add state search handler
  const handleStateSearch = (value: string) => {
    if (!value) {
      setStateSuggestions([]);
      setShowStateSuggestions(false);
      return;
    }

    const searchValue = value.toLowerCase();
    const regions = getRegionsForCountry(shipmentData.custom_address.country);
    const matches = regions.filter(state =>
      state.name.toLowerCase().includes(searchValue) ||
      state.code.toLowerCase().includes(searchValue)
    );
    setStateSuggestions(matches);
    setShowStateSuggestions(true);
  };

  // Add state selection handler
  const handleStateSelect = (state: {code: string, name: string}) => {
    setShipmentData(prev => ({
      ...prev,
      custom_address: { ...prev.custom_address, state: state.code }
    }));
    setShowStateSuggestions(false);
  };

  // Modify handleSubmit to include custom address
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (shipmentData.use_custom_address && !validateCustomAddress()) {
      setError('Please fill in all required address fields');
      return;
    }

    // Stored-address path has no custom form to validate, but the saved street
    // can still be over UPS's per-line limit. Block submission (UPS error 120202)
    // and point the rep at the custom-address override with a proposed rewrite.
    if (!shipmentData.use_custom_address) {
      const customer = customers.find(c => c.customer_tag === shipmentData.customer_tag);
      const defaultStreet = customer?.stripe?.address_line1 ?? '';
      if (willUpsAddressTruncate(defaultStreet)) {
        setError(
          `This customer's saved street address exceeds UPS's ${UPS_ADDRESS_LINE_MAX}-character ` +
          `line limit. Enable "Use Custom Shipping Address" and enter a shorter address ` +
          `(suggested: "${proposeUpsAddress(defaultStreet)}").`
        );
        return;
      }
    }

    // Validate all quantities before submitting
    const errors: {[key: number]: string} = {};
    if (!shipmentData.is_return) {  // Only validate for outgoing shipments
      shipmentData.products.forEach((product, index) => {
        const inventoryProduct = products.find(p => p.product_name === product.product_name);
        if (inventoryProduct) {
          const available = inventoryProduct.available || 0;
          if (product.quantity > available) {
            errors[index] = `Exceeds available inventory (${available} available)`;
          }
        }
      });
    }

    if (Object.keys(errors).length > 0) {
      setQuantityErrors(errors);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Create shipment with all products
      const response = await fetch(`${API_BASE_URL}/api/create_shipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_tag: shipmentData.customer_tag,
          products: shipmentData.products.map(product => ({
            product_name: product.product_name,
            quantity: parseInt(product.quantity.toString(), 10),
            device_ids: product.device_ids || []
          })),
          is_return: shipmentData.is_return,
          is_physical_dropoff: shipmentData.is_physical_dropoff,
          force_ground_shipping: shipmentData.force_ground_shipping,
          shipping_speed: shipmentData.shipping_speed || '03',
          carrier: shipmentData.carrier || 'ups',
          service_code: shipmentData.service_code || shipmentData.shipping_speed || '03',
          service_name: shipmentData.service_name || '',
          is_broken: shipmentData.is_broken,
          create_inventory_order: shipmentData.create_inventory_order,
          use_custom_address: shipmentData.use_custom_address,
          custom_address: shipmentData.use_custom_address ? {
            name: shipmentData.custom_address.name,
            street1: shipmentData.custom_address.street1,
            street2: shipmentData.custom_address.street2 || '',
            city: shipmentData.custom_address.city,
            state: shipmentData.custom_address.state,
            postal_code: shipmentData.custom_address.postal_code,
            country: shipmentData.custom_address.country || 'US'
          } : null,
          customs_declaration: shipmentData.customs_declaration,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create shipment');
      }

      const data = await response.json();

      // Carrier was switched: keep the notice on the page until dismissed
      setFallbackNotice(data.fallback && data.fallback_message
        ? { message: data.fallback_message, reason: data.fallback_reason || '' }
        : null);

      // Reset form
      setShipmentData({
        customer_tag: '',
        products: [{ product_name: '', quantity: 0, lineId: nextLineId() }],
        is_return: false,
        is_physical_dropoff: false,
        force_ground_shipping: false,
        shipping_speed: '',
        carrier: '',
        service_code: '',
        service_name: '',
        is_broken: false,
        create_inventory_order: true,
        use_custom_address: false,
        custom_address: {
          name: '',
          street1: '',
          street2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'US'
        },
        customs_declaration: null
      });

      // Refresh shipments
      await fetchShipments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shipment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !products.length || customersLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  if (error) {
    return <Alert type="error">{error}</Alert>;
  }

  return (
    <div className="shipment-page">
      <div className="shipment-form-container">
        <h2>Create New Shipment</h2>
        {fallbackNotice && (
          <Alert type="warning" onDismiss={() => setFallbackNotice(null)}>
            <strong>{fallbackNotice.message}</strong>
            {fallbackNotice.reason && <div>Reason: {fallbackNotice.reason}</div>}
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="shipment-form">
          <div className="form-group customer-select">
            <label htmlFor="customer">Select Customer</label>
            <CustomerSelect
              id="customer"
              value={shipmentData.customer_tag}
              onChange={handleCustomerSelect}
              options={[...customers]
                .sort((a, b) => displayName(a).localeCompare(displayName(b)))
                .map(c => ({ value: c.customer_tag, label: displayName(c) }))}
              placeholder="Search customers..."
              required
            />
          </div>

          {shipmentData.customer_tag && (
            <>
              {!shipmentData.use_custom_address && (() => {
                const customer = customers.find(c => c.customer_tag === shipmentData.customer_tag);
                const customerName = customer?.stripe?.name ?? '';
                if (!willUpsTruncate(customerName)) return null;
                return (
                  <p className="ups-name-warning">
                    Note: &ldquo;{customerName}&rdquo; exceeds UPS&rsquo;s {UPS_NAME_MAX}-character
                    label limit. The label will print as:{' '}
                    <strong>&ldquo;{clipUpsName(customerName)}&rdquo;</strong>.
                    Enable <em>Use Custom Shipping Address</em> below to override.
                  </p>
                );
              })()}
              {!shipmentData.use_custom_address && (() => {
                const customer = customers.find(c => c.customer_tag === shipmentData.customer_tag);
                const defaultStreet = customer?.stripe?.address_line1 ?? '';
                if (!willUpsAddressTruncate(defaultStreet)) return null;
                return (
                  <p className="ups-name-warning">
                    Note: this customer&rsquo;s saved street address ({defaultStreet.trim().length} chars)
                    exceeds UPS&rsquo;s {UPS_ADDRESS_LINE_MAX}-character line limit, so UPS will reject the label.
                    Suggested:{' '}
                    <strong>&ldquo;{proposeUpsAddress(defaultStreet)}&rdquo;</strong>.
                    Enable <em>Use Custom Shipping Address</em> below to enter a shorter address.
                  </p>
                );
              })()}
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={shipmentData.use_custom_address}
                    onChange={(e) => {
                      setShipmentData(prev => ({
                        ...prev,
                        use_custom_address: e.target.checked
                      }));
                    }}
                  />
                  Use Custom Shipping Address
                </label>
              </div>

              {shipmentData.use_custom_address && (
                <div className="custom-address-form">
                  <h3>Custom Shipping Address</h3>
                  <div className="form-group">
                    <label htmlFor="custom-country">Country</label>
                    <select
                      id="custom-country"
                      value={shipmentData.custom_address.country}
                      onChange={(e) => {
                        const newCountry = e.target.value;
                        setShipmentData(prev => ({
                          ...prev,
                          custom_address: { ...prev.custom_address, country: newCountry, state: '' },
                          customs_declaration: newCountry !== 'US' ? {
                            products: prev.products
                              .filter(p => p.product_name)
                              .map(p => ({ description: p.product_name, quantity: p.quantity, unit_value: 150, hs_code: '' })),
                            reason_for_export: 'SALE',
                          } : null,
                        }));
                        setShippingRates([]);
                      }}
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="custom-name">Name</label>
                    <input
                      id="custom-name"
                      type="text"
                      value={shipmentData.custom_address.name}
                      onChange={(e) => {
                        setShipmentData(prev => ({
                          ...prev,
                          custom_address: { ...prev.custom_address, name: e.target.value }
                        }));
                      }}
                      required
                    />
                    {willUpsTruncate(shipmentData.custom_address.name) && (
                      <p className="ups-name-warning">
                        Note: UPS labels limit recipient names to {UPS_NAME_MAX} characters.
                        This will print on the label as:{' '}
                        <strong>&ldquo;{clipUpsName(shipmentData.custom_address.name)}&rdquo;</strong>.
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="custom-street1">Street Address</label>
                    <input
                      id="custom-street1"
                      type="text"
                      value={shipmentData.custom_address.street1}
                      onChange={(e) => {
                        setShipmentData(prev => ({
                          ...prev,
                          custom_address: { ...prev.custom_address, street1: e.target.value }
                        }));
                      }}
                      required
                    />
                    {willUpsAddressTruncate(shipmentData.custom_address.street1) && (
                      <p className="ups-name-warning">
                        Note: UPS limits each address line to {UPS_ADDRESS_LINE_MAX} characters and will
                        reject this shipment ({shipmentData.custom_address.street1.trim().length} chars).
                        Suggested:{' '}
                        <strong>&ldquo;{proposeUpsAddress(shipmentData.custom_address.street1)}&rdquo;</strong>
                        {willUpsAddressTruncate(proposeUpsAddress(shipmentData.custom_address.street1))
                          ? ' - still too long; please shorten further before continuing.'
                          : '. Please update the street address to continue.'}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="custom-street2">Apartment, suite, etc.</label>
                    <input
                      id="custom-street2"
                      type="text"
                      value={shipmentData.custom_address.street2}
                      onChange={(e) => {
                        setShipmentData(prev => ({
                          ...prev,
                          custom_address: { ...prev.custom_address, street2: e.target.value }
                        }));
                      }}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="custom-city">City</label>
                      <input
                        id="custom-city"
                        type="text"
                        value={shipmentData.custom_address.city}
                        onChange={(e) => {
                          setShipmentData(prev => ({
                            ...prev,
                            custom_address: { ...prev.custom_address, city: e.target.value }
                          }));
                        }}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="custom-state">{getRegionLabel(shipmentData.custom_address.country)}</label>
                      <div className="state-input-container">
                        <input
                          id="custom-state"
                          type="text"
                          value={shipmentData.custom_address.state}
                          onChange={(e) => {
                            const value = e.target.value;
                            setShipmentData(prev => ({
                              ...prev,
                              custom_address: { ...prev.custom_address, state: value }
                            }));
                            handleStateSearch(value);
                          }}
                          onFocus={() => handleStateSearch(shipmentData.custom_address.state)}
                          onBlur={() => setTimeout(() => setShowStateSuggestions(false), 200)}
                          placeholder={`Start typing ${getRegionLabel(shipmentData.custom_address.country).toLowerCase()} name or code`}
                          required
                        />
                        {showStateSuggestions && stateSuggestions.length > 0 && (
                          <div className="state-suggestions">
                            {stateSuggestions.map((state) => (
                              <div
                                key={state.code}
                                className="state-suggestion-item"
                                onClick={() => handleStateSelect(state)}
                              >
                                {state.name} ({state.code})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="custom-postal">{getPostalLabel(shipmentData.custom_address.country)}</label>
                      <input
                        id="custom-postal"
                        type="text"
                        value={shipmentData.custom_address.postal_code}
                        onChange={(e) => {
                          setShipmentData(prev => ({
                            ...prev,
                            custom_address: { ...prev.custom_address, postal_code: e.target.value }
                          }));
                        }}
                        required
                      />
                    </div>
                  </div>

                  {shipmentData.custom_address.country !== 'US' && shipmentData.customs_declaration && (
                    <div className="customs-declaration-form">
                      <h4>Customs Declaration</h4>
                      <div className="form-group">
                        <label htmlFor="export-reason">Reason for Export</label>
                        <select
                          id="export-reason"
                          value={shipmentData.customs_declaration.reason_for_export}
                          onChange={(e) => {
                            setShipmentData(prev => ({
                              ...prev,
                              customs_declaration: prev.customs_declaration
                                ? { ...prev.customs_declaration, reason_for_export: e.target.value }
                                : null,
                            }));
                          }}
                        >
                          {EXPORT_REASONS.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                      {shipmentData.customs_declaration.products.map((item, idx) => (
                        <div key={idx} className="customs-item">
                          <div className="form-row">
                            <div className="form-group" style={{flex: 2}}>
                              <label>Description</label>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => {
                                  setShipmentData(prev => {
                                    if (!prev.customs_declaration) return prev;
                                    const products = [...prev.customs_declaration.products];
                                    products[idx] = { ...products[idx], description: e.target.value };
                                    return { ...prev, customs_declaration: { ...prev.customs_declaration, products } };
                                  });
                                }}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Qty</label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => {
                                  setShipmentData(prev => {
                                    if (!prev.customs_declaration) return prev;
                                    const products = [...prev.customs_declaration.products];
                                    products[idx] = { ...products[idx], quantity: parseInt(e.target.value) || 1 };
                                    return { ...prev, customs_declaration: { ...prev.customs_declaration, products } };
                                  });
                                }}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Unit Value (USD)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_value}
                                onChange={(e) => {
                                  setShipmentData(prev => {
                                    if (!prev.customs_declaration) return prev;
                                    const products = [...prev.customs_declaration.products];
                                    products[idx] = { ...products[idx], unit_value: parseFloat(e.target.value) || 0 };
                                    return { ...prev, customs_declaration: { ...prev.customs_declaration, products } };
                                  });
                                }}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>HS Code (optional)</label>
                              <input
                                type="text"
                                value={item.hs_code}
                                onChange={(e) => {
                                  setShipmentData(prev => {
                                    if (!prev.customs_declaration) return prev;
                                    const products = [...prev.customs_declaration.products];
                                    products[idx] = { ...products[idx], hs_code: e.target.value };
                                    return { ...prev, customs_declaration: { ...prev.customs_declaration, products } };
                                  });
                                }}
                                placeholder="e.g. 8471.30"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="success"
                    size="lg"
                    onClick={handleConfirmCustomAddress}
                    className="confirm-address-button"
                    disabled={!validateCustomAddress() || !validateProducts()}
                  >
                    Update Shipping Rates
                  </Button>
                </div>
              )}

              <div className="checkbox-group return-label-group">
                <label>
                  <input
                    type="checkbox"
                    checked={shipmentData.is_return}
                    onChange={(e) => setShipmentData(prev => ({
                      ...prev,
                      is_return: e.target.checked,
                      shipping_speed: e.target.checked ? '03' : prev.shipping_speed, // Set to Ground if return
                      force_ground_shipping: e.target.checked,
                      is_broken: e.target.checked ? prev.is_broken : false, // Reset is_broken if return is unchecked
                      products: clearDeviceIdsOnDirectionChange(prev.products)
                    }))}
                  />
                  Return Label
                </label>
                
                {shipmentData.is_return && (
                  <label>
                    <input
                      type="checkbox"
                      checked={shipmentData.is_broken}
                      onChange={(e) => setShipmentData(prev => ({
                        ...prev,
                        is_broken: e.target.checked
                      }))}
                    />
                    <span className={shipmentData.is_broken ? 'broken-label' : ''}>
                      Mark as Broken
                    </span>
                  </label>
                )}
                
                <label>
                  <input
                    type="checkbox"
                    checked={shipmentData.is_physical_dropoff}
                    onChange={(e) => setShipmentData({
                      ...shipmentData,
                      is_physical_dropoff: e.target.checked,
                      is_return: e.target.checked ? false : shipmentData.is_return,
                      force_ground_shipping: e.target.checked ? false : shipmentData.force_ground_shipping,
                      shipping_speed: e.target.checked ? '' : shipmentData.shipping_speed,
                      is_broken: e.target.checked ? false : shipmentData.is_broken,
                      products: clearDeviceIdsOnDirectionChange(shipmentData.products)
                    })}
                  />
                  Physical Drop-off (No Shipping Label)
                </label>
                
                <label>
                  <input
                    type="checkbox"
                    checked={shipmentData.create_inventory_order}
                    onChange={(e) => setShipmentData(prev => ({
                      ...prev,
                      create_inventory_order: e.target.checked
                    }))}
                  />
                  Create inventory order
                </label>
              </div>
            </>
          )}

          <div className="products-container">
            <h3>Products</h3>
            {shipmentData.is_return && shipmentData.customer_tag && (
              <ReturnInventoryPicker
                customerTag={shipmentData.customer_tag}
                packages={packages}
                productNameForSku={sku => products.find(p => p.sku_code === sku)?.product_name}
                lines={shipmentData.products}
                onChange={applyReturnLines}
              />
            )}
            {packages.length > 0 && !shipmentData.is_return && (
              <div className="package-picker">
                <label htmlFor="apply-package">Apply Package</label>
                <select
                  id="apply-package"
                  value={selectedPackageId}
                  onChange={(e) => applyPackage(e.target.value)}
                >
                  <option value="">Select package…</option>
                  {packages.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name} ({pkg.items.length} item{pkg.items.length === 1 ? '' : 's'})
                    </option>
                  ))}
                </select>
                <span className="package-picker-hint">Replaces current products</span>
              </div>
            )}
            {shipmentData.products.map((product, index) => (
              <div key={product.lineId} className="product-row">
                <div className="form-group">
                  <label htmlFor={`product-${index}`}>Select Product</label>
                  <select
                    id={`product-${index}`}
                    value={product.product_name}
                    onChange={(e) => updateProduct(index, 'product_name', e.target.value)}
                    required
                  >
                    <option value="">Choose a product...</option>
                    {[...products]
                      .filter((p) => p.product_name === product.product_name ||
                                     !productNamesInUse.includes(p.product_name))
                      .sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''))
                      .map((p) => (
                      <option key={p.sku_code} value={p.product_name}>
                        {p.product_name} {!shipmentData.is_return && p.available !== undefined ? `(${p.available} available)` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor={`quantity-${index}`}>Qty</label>
                  <input
                    id={`quantity-${index}`}
                    type="number"
                    min="1"
                    value={product.quantity || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateProduct(index, 'quantity', val === '' ? 0 : parseInt(val, 10));
                    }}
                    required
                    className={quantityErrors[index] ? 'error' : ''}
                  />
                  {quantityErrors[index] && (
                    <span className="error-message">{quantityErrors[index]}</span>
                  )}
                </div>

                <DeviceIdPicker
                  customerTag={shipmentData.customer_tag}
                  skuCode={products.find(p => p.product_name === product.product_name)?.sku_code}
                  quantity={product.quantity}
                  mode={shipmentData.is_return ? 'out' : 'available'}
                  value={product.device_ids || []}
                  onChange={(device_ids) => {
                    // Functional update throughout: on a customer change every
                    // line's picker clears itself in the same commit, and a
                    // copy taken from the render closure would let the last
                    // one overwrite the others' clears.
                    setShipmentData(prev => {
                      const next = [...prev.products];
                      next[index] = { ...next[index], device_ids };
                      return { ...prev, products: next };
                    });
                  }}
                />

                {index > 0 && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => removeProduct(index)}
                    className="remove-product-button"
                    title="Remove Product"
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
            
            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={addProduct}
              className="add-product-button"
            >
              <span>+</span> Add Another Product
            </Button>
          </div>

          {shipmentData.customer_tag && !shipmentData.is_physical_dropoff && (
            <div className="shipping-speeds-container">
              <h3>Select Delivery Date</h3>
              {/* Add destination address */}
              {(() => {
                const customer = customers.find(c => c.customer_tag === shipmentData.customer_tag);
                if (!customer) return null;

                const address = shipmentData.use_custom_address
                  ? {
                      name: shipmentData.custom_address.name,
                      line1: shipmentData.custom_address.street1,
                      line2: shipmentData.custom_address.street2,
                      city: shipmentData.custom_address.city,
                      state: shipmentData.custom_address.state,
                      postal_code: shipmentData.custom_address.postal_code
                    }
                  : {
                      name: customer.stripe?.name,
                      line1: customer.stripe?.address_line1,
                      line2: customer.stripe?.address_line2,
                      city: customer.stripe?.address_city,
                      state: customer.stripe?.address_state,
                      postal_code: customer.stripe?.address_postal_code
                    };

                return (
                  <div className="destination-address">
                    <div className="address-label">{shipmentData.is_return ? 'Shipping from:' : 'Shipping to:'}</div>
                    <div className="address-details">
                      <strong>{address.name}</strong>
                      <span className="address-line">{address.line1}</span>
                      {address.line2 && <span className="address-line">{address.line2}</span>}
                      <span className="address-line">
                        {address.city}, {address.state} {address.postal_code}
                        {shipmentData.use_custom_address && shipmentData.custom_address.country !== 'US' &&
                          `, ${COUNTRIES.find(c => c.code === shipmentData.custom_address.country)?.name || shipmentData.custom_address.country}`
                        }
                      </span>
                    </div>
                  </div>
                );
              })()}
              {ratesLoading ? (
                <LoadingSpinner message="Loading shipping rates..." inline />
              ) : (
                <div className="shipping-timeline">
                  <div className="timeline-connector"></div>
                  {(() => {
                    const today = new Date();
                    const days: Date[] = [];
                    // Generate next 7 days
                    for (let i = 0; i < 7; i++) {
                      const date = new Date();
                      date.setDate(today.getDate() + i);
                      days.push(date);
                    }

                    return days.map((date, index) => {
                      const isToday = index === 0;
                      const deliveryOptions = shippingRates
                        .filter(rate => {
                          const rateDate = new Date(rate.delivery_date);
                          return rateDate.toDateString() === date.toDateString();
                        })
                        .sort((a, b) => a.total_charges - b.total_charges);

                      const dayHasDelivery = deliveryOptions.length > 0;
                      
                      return (
                        <div
                          key={date.toISOString()}
                          className={`timeline-day ${isToday ? 'today' : ''} ${
                            isWeekend(date) ? 'weekend' : ''
                          } ${dayHasDelivery ? 'delivery-date' : ''}`}
                        >
                          <div className="weekday">{getDayName(date)}</div>
                          <div className="date">{getMonthDay(date)}</div>
                          {deliveryOptions.map((rate: MultiCarrierRate) => {
                            const isSelected = shipmentData.carrier === rate.carrier
                              && shipmentData.service_code === rate.service_code;

                            return (
                              <div
                                key={`${rate.carrier}-${rate.service_code}`}
                                className={`delivery-option ${isSelected ? 'selected' : ''} ${rate.is_recommended ? 'recommended' : ''}`}
                                onClick={() => {
                                  setShipmentData(prev => ({
                                    ...prev,
                                    carrier: rate.carrier,
                                    service_code: rate.service_code,
                                    service_name: rate.service_name,
                                    shipping_speed: rate.service_code,
                                  }));
                                }}
                              >
                                <div className="price">{formatCurrency(rate.total_charges)}</div>
                                <div className="service-name">{rate.service_name}</div>
                                {rate.is_cheapest_in_tier && <span className="best-price-badge">Best Price</span>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" className="submit-button" loading={submitting}>
            {shipmentData.is_physical_dropoff ? 'Create Shipment Record' : 'Create Shipment'}
          </Button>
        </form>
      </div>

      {/* Separate container for shipments table */}
      <div className="shipments-section">
        <div className="shipments-table">
          <div className="shipments-header">
            <h2>Shipment History</h2>
            <div className="shipments-header-actions">
              <label className="toggle-delivered">
                <input
                  type="checkbox"
                  checked={showDelivered}
                  onChange={(e) => setShowDelivered(e.target.checked)}
                />
                Show delivered
              </label>
              <Button
                type="button"
                variant="primary"
                onClick={handleRefreshAll}
                disabled={refreshing}
              >
              {refreshing ? 'Refreshing...' : 'Refresh All'}
            </Button>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="tracking-col">Tracking</th>
                  <th>Actions</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Direction</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th>Shipped Date</th>
                  <th>Est. Arrival</th>
                  <th>Service Code</th>
                </tr>
              </thead>
              <tbody>
                {shipments
                  .filter((s) => showDelivered || s.status !== 'Delivered')
                  .sort((a, b) => new Date(b.shipped_date).getTime() - new Date(a.shipped_date).getTime())
                  .map((shipment) => {
                    const customer = customers.find(c => c.customer_tag === shipment.customer_tag);
                    const product = products.find(p => p.sku_code === shipment.sku_code);
                    const isDropoff = shipment.tracking_number.startsWith("DROPOFF-");
                    const isPendingDropoff = isDropoff && shipment.status === 'Pending Drop-off';
                    const isDropoffPending = isDropoff && shipment.status === 'Pending Drop-off';
                    const isDropoffDelivered = isDropoff && shipment.status === 'Delivered';
                    const isDelivered = shipment.status === 'Delivered';
                    const isOnTheWay = shipment.status === 'On the Way';
                    
                    return (
                      <tr 
                        key={shipment.tracking_number}
                        className={isDelivered ? 'delivered' : ''}
                      >
                        <td>
                          {isDropoff ? (
                            <span>DROPOFF</span>
                          ) : (
                            <a
                              href={getTrackingUrl(shipment.tracking_number, shipment.carrier)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tracking-link"
                              title={shipment.tracking_number}
                            >
                              …{shipment.tracking_number.slice(-5)}
                            </a>
                          )}
                        </td>
                        <td>
                          {isDropoffPending && (
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={() => handleUpdateDropoffStatus(shipment.tracking_number)}
                              title="Mark as Delivered"
                              className="update-status-button"
                            >
                              ✓
                            </Button>
                          )}
                          {isDropoffDelivered && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevertDropoff(shipment.tracking_number)}
                              title="Revert to Pending"
                              className="revert-button"
                            >
                              ↺
                            </Button>
                          )}
                          {!isDropoff && !isDelivered && (
                            <button
                              className="download-button"
                              title="Download Label"
                              onClick={() => handleDownloadLabel(shipment.tracking_number)}
                            >
                              ⬇
                            </button>
                          )}
                          {(shipment.status === 'Pending Drop-off' ||
                            shipment.status === 'Ready for UPS' ||
                            shipment.status === 'Label Created' ||
                            shipment.status === 'Shipment Ready for UPS') && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteShipment(shipment.tracking_number)}
                              title="Void Shipment"
                              className="delete-button"
                            >
                              ✕
                            </Button>
                          )}
                        </td>
                        <td>{customer ? displayName(customer) : shipment.customer_tag}</td>
                        <td>{product?.product_name || shipment.sku_code}</td>
                        <td><span className={`direction-badge ${shipment.is_return ? 'in' : 'out'}`}>{shipment.is_return ? 'IN' : 'OUT'}</span></td>
                        <td>{formatCurrency(shipment.cost)}</td>
                        <td className="status-cell">
                          {shipment.status === 'Shipment Ready for UPS' ||
                           shipment.status === 'Pending Drop-off' ||
                           shipment.status === 'Label Created' ? (
                            <span className="status">{shipment.status}</span>
                          ) : (
                            <span className="current-location">
                              📍 {shipment.current_location || shipment.status}
                            </span>
                          )}
                        </td>
                        <td>{formatDateTime(shipment.shipped_date)}</td>
                        <td>{formatDateTime(shipment.estimated_arrival)}</td>
                        <td>{formatServiceCode(shipment.service_code, shipment.service_name)}</td>
                      </tr>
                    );
                  })}
                {shipments.filter((s) => showDelivered || s.status !== 'Delivered').length === 0 && (
                  <tr><td colSpan={10} className="empty-state">No active shipments</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentPage; 